import { useMemo } from 'react'

import {
  useAccessTokenRoles,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import type {
  BidStatus,
  OrchestratedAuction,
  OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  getSelectedQuoteQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { parseOptionalISODate } from '@b-stock/fe-core/bstock-react'
import { LocationAttribute } from '@b-stock/location-api-client'

import { nextInstant, useRerenderAt } from '@/hooks/useRerenderAt'

import { AuctionState, BuyNowState } from './const'
import { useIsUserEligibleToBid } from './useIsUserEligibleToBid'

export type AuctionDetailState = {
  qualifiedToBid: boolean // selectedQuote true
  eligibleToBid: boolean // user has id which differs from listing.accountId and user has active buyer role
  bidState: BidStatus
  auctionState: AuctionState
  buyNowState: BuyNowState
  buyNowBid: boolean
}

type AuctionTiming = Pick<
  OrchestratedAuction,
  'actualEndTime' | 'buyNow' | 'canceled' | 'closed' | 'startTime'
>

const isPast = (iso: string | undefined, now: number) => {
  const date = parseOptionalISODate(iso)
  return !!date && date.getTime() <= now
}

/** The auction's and its Buy Now's state at `now`. */
export const getAuctionState = (auction: AuctionTiming, now: number) => {
  const isBuyNowEnabled = auction.buyNow?.enabled
  const buyNowStarted = isPast(auction.buyNow?.startTime, now)
  const buyNowEndTimePast = isPast(auction.buyNow?.endTime, now)
  const auctionStarted = isPast(auction.startTime, now)
  const auctionEndTimePast = isPast(auction.actualEndTime, now)

  let auctionState = AuctionState.Active
  let buyNowState = BuyNowState.Ended

  if (!auctionStarted) {
    /**
     * Before the auction starts, an enabled Buy Now is SCHEDULED until its
     * start time and ACTIVE after it.
     */
    if (isBuyNowEnabled) {
      if (buyNowStarted) {
        buyNowState = BuyNowState.Active
      } else {
        buyNowState = BuyNowState.Scheduled
      }
    }
    auctionState = AuctionState.Scheduled
  }

  if (
    auctionStarted &&
    isBuyNowEnabled &&
    buyNowStarted &&
    !buyNowEndTimePast &&
    !auction.closed &&
    !auction.canceled
  ) {
    buyNowState = BuyNowState.Active
  }

  if (auction.closed) {
    auctionState = AuctionState.Ended
  } else if (auction.canceled) {
    auctionState = AuctionState.Canceled
  } else if (auctionEndTimePast) {
    // If we're past the end time, we want to show a "Checking for final bids..." state.
    // There are two situations where the auction might still be open...
    // 1. Someone else bid at the last second, and popcorn bidding caused the auction to be
    //    extended, but the user hasn't got the pusher message yet letting them know about
    //    the new bid.
    // 2. The user's clock is out of sync with the server clock (this is a problem we will need
    //    to solve at some point, to minimize this client vs server clock offset)
    auctionState = AuctionState.Ending
  }

  return {
    auctionState,
    buyNowState,
  }
}

export const useAuctionBuyNowState = (auction: AuctionTiming) => {
  const now = Date.now() // oxlint-disable-line react/purity
  useRerenderAt(
    nextInstant(
      [
        auction.buyNow?.startTime,
        auction.buyNow?.endTime,
        auction.startTime,
        auction.actualEndTime,
      ].map((iso) => Date.parse(iso ?? '')),
      now
    )
  )
  return getAuctionState(auction, now)
}

export const useAuctionDetailState = (
  data: OrchestratedAuctionListingDetails
): AuctionDetailState => {
  const { accountId: sellerId, auction } = data
  const roles = useAccessTokenRoles()
  const isActiveBuyer = roles?.buyer === 'ACTIVE'

  const decodedToken = useDecodedAccessToken()
  const accountId = decodedToken?.accountId

  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        accountId: accountId ? [accountId] : [],
      },
    ],
    { enabled: isActiveBuyer }
  )
  const defaultLocation = myLocations.data?.locations[0]

  const { data: selectedQuote } = getSelectedQuoteQuery.useWithOpts(
    [data._id, accountId],
    {
      enabled:
        isActiveBuyer && (!!defaultLocation?.id || myLocations.isFetched),
    }
  )

  const eligibleToBid = useIsUserEligibleToBid({ sellerId })
  const qualifiedToBid = !!selectedQuote

  const { auctionState, buyNowState } = useAuctionBuyNowState(auction)

  return useMemo(
    () => ({
      eligibleToBid,
      qualifiedToBid,
      bidState: auction.bidStatus,
      buyNowBid: auction.bid?.buyNowBid || false,
      auctionState,
      buyNowState,
    }),
    [eligibleToBid, qualifiedToBid, auction, auctionState, buyNowState]
  )
}
